import React, { useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Button,
  Stack,
  Grid,
  Paper,
  Chip,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LandingHero from "./LandingHero";
import LandingImpactSection from "./LandingImpactSection";
import MarketingSiteFooter from "./MarketingSiteFooter";
import MarketingSiteHeader from "./MarketingSiteHeader";
import { scrollToAnchorBelowHeader } from "../utils/scrollToAnchor";
import { useAuth } from "../context/EnterpriseAuthContext";

const FEATURE_CARDS = [
  {
    title: "One workspace for the full deal",
    description:
      "Customers, projects, and meeting sessions live together so context stays with the opportunity—not lost across drives, chats, and inboxes.",
    Icon: HubOutlinedIcon,
  },
  {
    title: "Create from what you already have",
    description:
      "Start from transcripts, notes, requirements, and prior artifacts. You are not limited to a single source—use whatever your team already captured.",
    Icon: NoteAltOutlinedIcon,
  },
  {
    title: "Record & transcribe meetings",
    description:
      "Dispatch recording bots or bring in transcripts from tools you already use (where your plan supports it), then link sessions to the right project.",
    Icon: MicNoneOutlinedIcon,
  },
  {
    title: "Generate & edit with AI",
    description:
      "Draft proposals, SOWs, and other deliverables in rich text, then iterate with AI assistance so specialists review substance instead of fixing formatting.",
    Icon: SmartToyOutlinedIcon,
  },
  {
    title: "Action items & session insights",
    description:
      "Turn meetings into follow-ups you can act on—structured summaries and action lists so the next document reflects what was actually agreed.",
    Icon: AssignmentTurnedInOutlinedIcon,
  },
  {
    title: "Templates the whole team reuses",
    description:
      "Encode structure, approved language, and delivery checklists once. Everyone produces on-brand, client-ready documents the same way every time.",
    Icon: AutoAwesomeIcon,
  },
];

const HOW_IT_WORKS_STEPS = [
  {
    title: "Connect the work",
    description:
      "Organize customers, projects, and sessions in one workspace so every deliverable pulls from the right relationship and scope.",
  },
  {
    title: "Add source material",
    description:
      "Record or import meetings, paste notes, or lean on existing requirements—use whatever context your team already has.",
  },
  {
    title: "Generate, edit, and follow through",
    description:
      "Produce drafts from templates, refine with AI in the editor, and track action items without jumping between tools.",
  },
];


const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state: authState } = useAuth();
  const isSignedIn = Boolean(authState?.isAuthenticated && !authState?.requiresPasswordReset);
  const redirectToLogin = () => navigate("/login");

  useEffect(() => {
    const id = location.hash?.replace(/^#/, "");
    if (!id) return undefined;
    const timer = window.setTimeout(() => {
      scrollToAnchorBelowHeader(id);
    }, 150);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  const handleScrollTo = (targetId) => {
    scrollToAnchorBelowHeader(targetId);
  };

  // Signed-in visitors stay on the marketing page; its calls to action take them into the app.
  const handlePrimaryCTA = () => navigate(isSignedIn ? "/home" : "/login");

  return (
    <Box sx={{ bgcolor: "background.default", color: "text.primary" }}>
      <MarketingSiteHeader />

      <Box component="main">
        <LandingHero
          onGetStarted={handlePrimaryCTA}
          onWatchDemo={() => handleScrollTo("product-video")}
          onExploreUseCases={() => navigate("/use-cases")}
        />

        <LandingImpactSection />

        <Box id="use-cases" component="section" sx={{ py: { xs: 8, md: 10 }, backgroundColor: "background.default" }}>
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={7}>
                <Typography variant="overline" color="secondary" fontWeight={700}>
                  Use cases
                </Typography>
                <Typography variant="h4" fontWeight={800} sx={{ mt: 1, mb: 2 }}>
                  See how teams in your industry use QuillAI
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
                  We expanded use cases into a dedicated guide: consulting, SaaS, finance, legal, life sciences,
                  manufacturing, and more—each with typical friction points, how the workspace helps, and example
                  deliverables.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sessions stay tied to customers and projects; templates keep tone and structure consistent; your team
                  still reviews everything before it ships.
                </Typography>
              </Grid>
              <Grid item xs={12} md={5}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    textAlign: { xs: "left", md: "center" },
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                    Explore by industry
                  </Typography>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    fullWidth
                    onClick={() => navigate("/use-cases")}
                    endIcon={<ArrowForwardIcon />}
                    sx={{ textTransform: "none", fontWeight: 700, mb: 1 }}
                  >
                    Open use cases
                  </Button>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Deep dives with anchors you can share (e.g. legal, life sciences).
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Box
          id="features"
          component="section"
          sx={{ py: { xs: 8, md: 10 }, backgroundColor: "background.paper" }}
        >
          <Container maxWidth="xl">
            <Typography variant="overline" color="secondary" fontWeight={700}>
              Features
            </Typography>
            <Typography variant="h4" fontWeight={800} sx={{ mt: 1, mb: 1 }}>
              Your one-stop workplace for meetings, documents, and follow-through.
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 6 }}>
              QuillAI turns existing notes, sessions, and templates into client-ready deliverables—then helps you edit with
              AI, capture new calls, and surface action items in the same workspace.
            </Typography>
            <Grid container spacing={4}>
              {FEATURE_CARDS.map((feature) => (
                <Grid item xs={12} sm={6} md={4} key={feature.title}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                      height: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        backgroundColor: "secondary.main",
                        color: "secondary.contrastText",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2,
                      }}
                    >
                      <feature.Icon />
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        <Box
          id="how-it-works"
          component="section"
          sx={{ py: { xs: 8, md: 10 }, backgroundColor: "background.default" }}
        >
          <Container maxWidth="xl">
            <Typography variant="overline" color="secondary" fontWeight={700}>
              How it works
            </Typography>
            <Typography variant="h4" fontWeight={800} sx={{ mt: 1, mb: 6 }}>
              From scattered context to finished deliverables in three steps
            </Typography>
            <Grid container spacing={4} alignItems="center">
              {HOW_IT_WORKS_STEPS.map((step, index) => (
                <Grid item xs={12} md={4} key={step.title}>
                  <Box
                    sx={{
                      position: "relative",
                      p: 4,
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                      height: "100%",
                    }}
                  >
                    <Chip
                      label={`0${index + 1}`}
                      color="primary"
                      sx={{ position: "absolute", top: -20, left: 24, fontWeight: 700 }}
                    />
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1, mt: 2 }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {step.description}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        <Box
          id="pricing"
          component="section"
          sx={{ py: { xs: 8, md: 10 }, backgroundColor: "background.default" }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={7}>
                <Typography variant="overline" color="secondary" fontWeight={700}>
                  Pricing
                </Typography>
                <Typography variant="h4" fontWeight={800} sx={{ mt: 1, mb: 2 }}>
                  Simple plans that scale with templates, sessions, and documents
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
                  Free, Pro, Pro Plus, and Enterprise—the same tiers and limits you see in the product. Compare features
                  and limits on the full pricing page, then sign in to manage billing anytime.
                </Typography>
              </Grid>
              <Grid item xs={12} md={5}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                    View plans &amp; upgrade in-app
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={() => navigate("/pricing")}
                    endIcon={<ArrowForwardIcon />}
                    sx={{ textTransform: "none", fontWeight: 700, mb: 1 }}
                  >
                    Open pricing
                  </Button>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Starts at $0/mo—no credit card for the Free tier.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Box
          component="section"
          sx={{ py: { xs: 8, md: 10 }, backgroundColor: "background.paper" }}
        >
          <Container maxWidth="lg">
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: 4,
                background: "linear-gradient(135deg, #4A00E0, #F9484A)",
                color: "white",
                textAlign: "center",
              }}
            >
              <Typography variant="h4" fontWeight={800} sx={{ mb: 2 }}>
                Ready to generate your next client deliverable?
              </Typography>
              <Typography variant="body1" sx={{ mb: 1, maxWidth: 560, mx: "auto", opacity: 0.95 }}>
                Start free with no credit card—then turn meetings and notes into proposals, statements of work, timelines,
                and more.
              </Typography>
              <Stack spacing={2} alignItems="center" sx={{ mt: 3 }}>
                <Button
                  size="large"
                  variant="contained"
                  color="inherit"
                  onClick={handlePrimaryCTA}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ color: "primary.main", textTransform: "none", fontWeight: 700, px: 3 }}
                >
                  {isSignedIn ? "Open app" : "Start free"}
                </Button>
                {!isSignedIn && (
                  <Button
                    size="medium"
                    variant="text"
                    color="inherit"
                    onClick={redirectToLogin}
                    sx={{ textTransform: "none", opacity: 0.9 }}
                  >
                    Already have an account? Log in
                  </Button>
                )}
              </Stack>
            </Paper>
          </Container>
        </Box>
      </Box>

      <MarketingSiteFooter />
    </Box>
  );
};

export default LandingPage;

